<template>
  <div v-if="preflightLoading" class="relative text-center">
    <FaIcon class="mr-2 text-blue-700" name="spinner-third" variant="duotone" spin size="2x" />
  </div>

  <div v-else-if="errorMsg">
    <ElAlert type="error" class="mb-5" show-icon>{{ errorMsg }}</ElAlert>
    <AppButton
      type="primary"
      text
      bg
      native-type="submit"
      class="mt-6 w-full"
      @click="emit('switch-to-password')"
    >
      zum Login
    </AppButton>
  </div>

  <div v-else-if="hasTokenErrors" class="relative text-center">
    <ElAlert type="error" :title="errors.requestAccessToken[0]" show-icon />
    <AppButton
      type="primary"
      text
      bg
      native-type="submit"
      class="mt-6 w-full"
      @click="emit('switch-to-password')"
    >
      zum Login
    </AppButton>
  </div>

  <ElForm
    v-else
    class="flex flex-col gap-2"
    label-position="top"
    require-asterisk-position="right"
    @submit.prevent
  >
    <div>
      Für {{ duzen('deine', 'Ihre') }} Anmeldung ist ein weiterer Faktor notwendig. {{ twoFactorLabels[twoFactorMode] }}
    </div>

    <ElFormItem
      :label="twoFactorMode === TwoFactors.Birthdate ? 'Geburtsdatum' : 'Code/Recoverycode' "
      for="answer"
      :error="errors.answer?.[0]"
    >
      <ElInput
        id="answer"
        v-model="form.answer"
        :disabled="loading"
        autofocus
        :placeholder="twoFactorMode === TwoFactors.Birthdate? 'tt.mm.jjjj' : ''"
      />
    </ElFormItem>

    <div v-if="form.deviceToken" class="mt-4">
      <ElCheckbox
        id="tfa-remember-device-checkbox"
        v-model="form.remember"
        :disabled="loading"
        label="Gerät merken"
      />
      <div class="text-gray-600 text-xs">
        Überspringt zukünftig diese Zwei-Faktor Abfrage auf diesem Gerät.
      </div>
    </div>

    <AppButton
      type="primary"
      native-type="submit"
      :handler="submit"
      class="mt-3 w-full"
    >
      Anmelden
    </AppButton>
  </ElForm>
</template>

<script setup lang="ts">
import FaIcon from '@/application/components/ui/FaIcon.vue';
import AppButton from '@/application/components/ui/AppButton.vue';
import { ElAlert, ElCheckbox, ElForm, ElFormItem, ElInput } from 'element-plus';
import { useForm } from "@/api/lib/integration";
import { requestAccessTokenLogin } from "@/api/auth/authApi";
import { computed, onMounted, ref } from "vue";
import { getDeviceId, getDeviceName } from "@/application/utils/envInfo";
import { TwoFactors } from '@/api/auth/types';
import { useBaseDataStore } from '@/user/store/baseDataStore.ts';

// 1.1 token is invalid, check if old login exists
// 1.2 token is valid, check 2fa
// 2. logging in

interface Props {
  requestAccessToken: string,
}

const props = defineProps<Props>();

const { duzen } = useBaseDataStore();

const twoFactorLabels = {
  [TwoFactors.Fortitfy]: `Bitte ${duzen('gebe deinen', 'geben Sie Ihren')} Authenticator Code ein.`,
  [TwoFactors.Birthdate]: `Bitte ${duzen('bestätige dein', 'bestätigen Sie Ihr')} Geburtsdatum.`,
}

const emit = defineEmits(['logging-in', 'switch-to-password']);

const twoFactorMode = ref('');
const preflightLoading = ref(false);

const { errors, form, submit, errorMsg, loading } = useForm(
  requestAccessTokenLogin,
  {
    requestAccessToken: props.requestAccessToken,
    answer: undefined as string|undefined,
    deviceName: undefined as string|undefined,
    deviceToken: undefined as string|undefined,
    remember: false,
  },
    (data) => {
      if (data.twoFactor) {
        twoFactorMode.value = data.twoFactor;
        return;
      }

      emit('logging-in', data.accessToken, data.clientSecret);
    },
);

const hasTokenErrors = computed(() => errors.value.requestAccessToken ? errors.value.requestAccessToken.length > 0 : false);

onMounted(async () => {
  form.value.deviceName = (await getDeviceName()) ?? 'Generic device';
  form.value.deviceToken = await getDeviceId() ?? undefined;
  // initially send requestAccessToken to get 2FA challenge or login with accessToken
  preflightLoading.value = true;
  await submit();
  // enable tfa iput validation
  form.value.answer = '';
  preflightLoading.value = false;
});

defineExpose({ form, errors })
</script>
